/**
 * How much of each kind of request keeps its trace, inside the Sentry plan's span
 * quota (`docs/ops.md`).
 *
 * Three tiers. Where money moves for a shopper, every trace: a failure there has
 * to be explainable after the fact, and the volume is small. A background poll,
 * almost none: the panel's notification poll alone was most of the project's
 * spans. Everything else, cron runs included, a tenth; a cron failure is reported
 * as an event whether or not its trace was kept.
 *
 * Matched against every place the path can be at sampling time: the span name
 * (`POST /en/checkout` on the server, `/:locale/checkout` in the browser), the URL
 * attributes, and the request URL, because an incoming HTTP span can still be
 * named by its method alone when it is sampled.
 */
const FULL_TRACE_PATHS: readonly RegExp[] = [
	// Checkout, the payment page and its server actions.
	/\/checkout(?:\/|$)/,
	// The order a shopper returns to after paying.
	/\/account\/orders(?:\/|$)/,
	// Provider returns and provider webhooks.
	/^\/api\/(?:payments|webhooks)\//,
];

const POLL_PATHS: readonly RegExp[] = [
	/^\/api\/manage\/[^/]+\/notifications(?:\/|$)/,
	/^\/api\/cart\/track(?:\/|$)/,
	/^\/api\/landing\/track(?:\/|$)/,
];

/** The fraction of ordinary traces kept. */
export const DEFAULT_TRACE_RATE = 0.1;
/** The fraction of poll traces kept. */
const POLL_TRACE_RATE = 0.01;

/** True for a page where a shopper's money moves; the browser's replay filter reads it too. */
export function isMoneyPath(path: string): boolean {
	return FULL_TRACE_PATHS.some((re) => re.test(path));
}

interface SamplingInput {
	name: string;
	attributes?: Record<string, unknown>;
	normalizedRequest?: { url?: string };
}

const URL_ATTRIBUTES = ["url.path", "http.target", "http.route", "url.full", "http.url"] as const;

/** The tier's rate for this span, or `null` so the caller falls back to inheriting. */
export function traceRate(ctx: SamplingInput): number | null {
	const candidates = [ctx.name.replace(/^[A-Z]+ /, ""), ctx.normalizedRequest?.url];
	for (const key of URL_ATTRIBUTES)
		candidates.push(typeof ctx.attributes?.[key] === "string" ? ctx.attributes[key] : undefined);
	const paths = candidates.filter((v): v is string => Boolean(v)).map(toPath);
	if (paths.some(isMoneyPath)) return 1;
	if (paths.some((path) => POLL_PATHS.some((re) => re.test(path)))) return POLL_TRACE_RATE;
	return null;
}

function toPath(value: string): string {
	if (value.startsWith("/")) return value.split("?")[0];
	try {
		return new URL(value).pathname;
	} catch {
		return value;
	}
}
